import React, { useEffect, useState } from "react";
import Flatpickr from "react-flatpickr";
import CustomChartDemo from "./CustomChartDemo";
import styles from "./ClientDashboardDemoComponents.module.scss";
import axios from "axios";
import moment from "moment";
import {
  Button,
  ButtonGroup,
} from "reactstrap";
import CustomOnlyModal from "../../../../components/CustomModal/CustomOnlyModal.js";
import {
  areDatesOnSameDay,
  convertJsToUnixSeconds,
  convertUnixToJs,
  convertUnixToJsWithDateNoTime,
  getEndOfDay,
  getStartOfDay,
} from "../MonitoringComponents/Helper";

function PowerConsumptionChartDemo(props) {
  const { buildingId, userRole } = props;

  // --- Data mode switch ---
  const [dataMode, setDataMode] = useState('live');
  const isLiveData = dataMode === 'live';

  // --- Date Picker ---
  const [dateRange, setDateRange] = useState([
    getStartOfDay(new Date()),
    new Date(),
  ]);
  const [startPicker, endPicker] = dateRange;

  // --- Live Window Picker ---
  const [liveWindow, setLiveWindow] = useState('6h');
  const liveWindows = ['30m', '1h', '6h'];

  // --- Line Chart Data ---
  const [rawPowerData, setRawPowerData] = useState([]);
  const [mergedChartData, setMergedChartData] = useState([]);
  const [showTotal, setShowTotal] = useState(false);
  const [dataProcessingStatus, setDataProcessingStatus] = useState('loading');
  const [expandOpen, setExpandOpen] = useState(false);
  const [downloadMonth, setDownloadMonth] = useState(() => moment().subtract(1, "month").format("YYYY-MM"));
  const [downloadStatus, setDownloadStatus] = useState("");

  const completedMonthOptions = Array.from({ length: 12 }, (_, index) => {
    const month = moment().subtract(index + 1, "month");
    return {
      value: month.format("YYYY-MM"),
      label: month.format("MMMM YYYY"),
    };
  });

  // Utility: convert live window to seconds
  const getWindowInSeconds = (window) => {
    switch (window) {
      case "30m": return 30 * 60;
      case "1h": return 60 * 60;
      case "6h": return 6 * 60 * 60;
      default: return 6 * 60 * 60;
    }
  }

  const downloadCsv = (filename, rows) => {
    const blob = new Blob([rows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const escapeCsvValue = (value) => {
    if (value === null || value === undefined) return "";
    const stringValue = String(value);
    if (/[",\n\r]/.test(stringValue)) {
      return `"${stringValue.replace(/"/g, '""')}"`;
    }
    return stringValue;
  };

  const handleDownloadMonth = async () => {
    if (!downloadMonth) return;

    setDownloadStatus("Preparing...");
    try {
      const selectedMonth = moment(downloadMonth, "YYYY-MM");
      const startTimestamp = selectedMonth.clone().startOf("month").unix();
      const endTimestamp = selectedMonth.clone().endOf("month").unix();
      const endpoint = `getPowerData${userRole === "admin" ? "Admin" : ""}`;

      const response = await axios.get(`/assets/${endpoint}`, {
        params: {
          buildingId: buildingId,
          startTime: startTimestamp,
          endTime: endTimestamp,
        },
      });

      const rows = Array.isArray(response.data) ? response.data : [];
      if (!rows.length) {
        setDownloadStatus("No data");
        return;
      }

      const valueKeys = Array.from(
        rows.reduce((keys, row) => {
          Object.keys(row).forEach((key) => {
            if (key !== "batch") keys.add(key);
          });
          return keys;
        }, new Set())
      ).sort((a, b) => {
        if (a === "total") return 1;
        if (b === "total") return -1;
        return a.localeCompare(b);
      });

      const header = ["Timestamp", ...valueKeys.map((key) => key.replace("_value", ""))];
      const csvRows = [
        header.map(escapeCsvValue).join(","),
        ...rows.map((row) => {
          const timestamp = row.batch ? moment.unix(row.batch).format("YYYY-MM-DD HH:mm:ss") : "";
          const values = valueKeys.map((key) => {
            const value = row[key];
            return value === null || value === undefined || value === ""
              ? ""
              : Number.isFinite(Number(value))
              ? (Number(value) / 1000).toFixed(2)
              : value;
          });
          return [timestamp, ...values].map(escapeCsvValue).join(",");
        }),
      ];

      downloadCsv(
        `power-${buildingId}-${selectedMonth.format("YYYY-MM")}.csv`,
        csvRows
      );
      setDownloadStatus("Downloaded");
    } catch (error) {
      console.error("Error downloading monthly power data", error);
      setDownloadStatus("Failed");
    }
  };

  // Fetches power data upon date change or every 30s
  useEffect(() => {
    const fetchPowerData = async (selectedValue, startTimestamp, endTimestamp) => {
      const endpoint = `getPowerData${userRole === "admin" ? "Admin" : ""}`;

      const currentPower = await axios.get(`/assets/${endpoint}`, {
        params: {
          buildingId: buildingId,
          startTime: startTimestamp,
          endTime: endTimestamp,
        },
      });

      if (currentPower.data.length === 0) {
        setDataProcessingStatus('no data');
        return false;
      }

      if (currentPower.status === 200) {
        setDataProcessingStatus('loading');
        if (!isLiveData) {
          // Historical: keep only first 10000 points
          setRawPowerData(currentPower.data.slice(0, 10000));
        } else {
          setRawPowerData(currentPower.data);
        }
        return true;
      }

      setDataProcessingStatus('no data');
      return false;
    }

    const retrievePowerData = async () => {
      setDataProcessingStatus('loading');

      const today = new Date();
      // In live mode, always use current time; in historical mode, use selected date
      const endDate = isLiveData ? today : (areDatesOnSameDay(endPicker, today) ? today : endPicker);

      const endTimestamp = convertJsToUnixSeconds(endDate);
      if (isLiveData) {
        const startTimestamp = endTimestamp - getWindowInSeconds(liveWindow);
        await fetchPowerData(buildingId, startTimestamp, endTimestamp);
      } else {
        const startTimestamp = convertJsToUnixSeconds(startPicker);
        await fetchPowerData(buildingId, startTimestamp, endTimestamp);
      }
    };

    retrievePowerData();

    let intervalId;
    if (isLiveData) {
      intervalId = setInterval(() => {
        retrievePowerData();
      }, 30000);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [buildingId, isLiveData, dateRange, endPicker, startPicker, userRole, liveWindow]);

  // Processes data (no aggregation)
  useEffect(() => {
    const transformPowerDataForChart = (powerData) => {
      let total = [];
      const jsTimeFunc = isLiveData ? convertUnixToJs : convertUnixToJsWithDateNoTime;

      const resultArray = powerData.map((obj) => {
        let o = {};
        for (let key in obj) {
          if (key === "batch") {
            o["jsTime"] = jsTimeFunc(obj[key]);
          } else if (key === "total") {
            total.push({
              total: parseFloat(Number(obj[key] / 1000).toFixed(2)),
              jsTime: jsTimeFunc(obj.batch),
            });
          } else {
            o[key.replace("_value", "")] = parseFloat(Number(obj[key] / 1000).toFixed(2));
          }
        }
        return o;
      });

      // Calculate total if no total value in resultArray
      if (
        total.length === 0 &&
        resultArray.length > 0 &&
        Object.keys(resultArray[0]).length > 2 &&
        !resultArray[0].total
      ) {
        total = resultArray.map((obj) => {
          return {
            total: Number(
              Object.values(obj)
                .reduce((acc, curr) => acc + (typeof curr === "number" ? curr : 0), 0)
                .toFixed(2)
            ),
            jsTime: obj.jsTime,
          };
        });
      }

      return [resultArray, total];
    }
    
    if (!rawPowerData || rawPowerData.length === 0) return { lineChartData: [], lineChartTotal: [] };
    setDataProcessingStatus('loading');

    const [resultArray, total] = transformPowerDataForChart(rawPowerData);

    const merged = resultArray.map(item => {
      const totalItem = total.find(t => t.jsTime === item.jsTime);
      return totalItem ? { ...item, total: totalItem.total } : item;
    });
    setMergedChartData(merged);
    setDataProcessingStatus('loaded data');
  }, [isLiveData, rawPowerData]);
  console.log(isLiveData);
  // ----- End of Line Chart -----
  return (
    <div className={styles.PowerConsumptionChartDemoContainer}>
      <div className={styles.InputRow}>
        <ButtonGroup className={styles.CompactButtonGroup}>
          <Button
            color={dataMode === 'live' ? 'primary' : 'secondary'}
            onClick={() => {
              setDataMode('live');
              // Refresh time range when switching to live mode
              setDateRange([
                getStartOfDay(new Date()),
                new Date(),
              ]);
            }}
          >
            Live Data
          </Button>
          <Button
            color={dataMode === 'historical' ? 'primary' : 'secondary'}
            onClick={() => setDataMode('historical')}
          >
            Historical Data
          </Button>
        </ButtonGroup>
        {isLiveData && (
          <ButtonGroup className={styles.CompactButtonGroup} style={{ marginLeft: '1rem' }}>
            {liveWindows.map((window) => (
              <Button
                key={window}
                color={liveWindow === window ? 'primary' : 'secondary'}
                onClick={() => {
                  console.log(window);
                  setLiveWindow(window)
                }}
              >
                {window}
              </Button>
            ))}
          </ButtonGroup>
        )}
        <div style={{ marginLeft: 'auto' }}>
          <select
            aria-label="Download month"
            value={downloadMonth}
            onChange={(event) => {
              setDownloadMonth(event.target.value);
              setDownloadStatus("");
            }}
            style={{
              height: 22,
              maxWidth: 120,
              marginRight: 6,
              borderRadius: 4,
              border: "1px solid #555",
              background: "transparent",
              color: "var(--body-color)",
              fontSize: 12,
            }}
          >
            {completedMonthOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <button
            onClick={handleDownloadMonth}
            aria-label="Download monthly power data"
            title="Download completed month"
            style={{
              background: "#188efc",
              border: "1px solid #188efc",
              height: 22,
              borderRadius: 4,
              color: "#fff",
              fontSize: 12,
              lineHeight: 1.2,
              marginRight: 6,
              padding: "0 8px",
            }}
          >
            Download
          </button>
          {downloadStatus && (
            <span style={{ fontSize: 11, marginRight: 6 }}>{downloadStatus}</span>
          )}
          <button
            onClick={() => setExpandOpen(true)}
            aria-label="Expand chart"
            title="Expand"
            style={{
              background: '#ff6b6b', border: '1px solid rgba(255,255,255,0.2)',
              width: 28, height: 22, borderRadius: 6,
              color: '#fff', fontWeight: 700, fontSize: 12, lineHeight: '18px'
            }}
          >
            ⤢
          </button>
        </div>
      </div>
      <div className={styles.InputRow} style={{ justifyContent: 'flex-end' }}>
        
      </div>
      {!isLiveData && (
        <div className={styles.InputRow}>
          <Flatpickr
            className={`col-md-6 ${styles.DateRangePicker}`}
            options={{
              mode: "range",
              enableTime: false,
              dateFormat: "d-M-Y",
              minDate: moment().subtract(1, "month").startOf("day").toDate(),
              maxDate: moment().endOf("day").toDate(), 
            }}
            value={dateRange}
            onChange={([start, end]) => {
              if (start && end) {
                const today = new Date();
                const maxEnd = moment(start).add(7, 'days').endOf('day').toDate();
                const cappedEnd = maxEnd > today ? today : maxEnd;

                const adjustedEnd = areDatesOnSameDay(end, today)
                  ? today
                  : getEndOfDay(end);

                const finalEnd = adjustedEnd > cappedEnd ? cappedEnd : adjustedEnd;

                setDateRange([start, finalEnd]);
              }
            }}
          />
          {mergedChartData.some(d => 'total' in d) && (
            <label className={styles.DateRangePickerLabel}>
              <input
                type="checkbox"
                checked={showTotal}
                onChange={() => setShowTotal((prev) => !prev)}
                style={{ marginRight: '8px' }}
              />
              Show Total
            </label>
          )}
        </div>
      )}
      {(() => {
        switch (dataProcessingStatus) {
          case "loading":
            return <div>Loading data</div>;
          case "no data":
            return <div>No data found</div>;
          case "loaded data": {
            // Filter out total field if showTotal is false
            const filteredData = showTotal
              ? mergedChartData
              : mergedChartData.map(({ total, ...rest }) => rest);
            return (
              filteredData.length > 0 && filteredData.every(d => d.jsTime) && (
                <>
                  <div className={styles.LineChartContainer}>
                    <CustomChartDemo
                      lineChartData={filteredData}
                      unit={"kW"}
                    />
                  </div>
                  <CustomOnlyModal
                    modalStatus={expandOpen}
                    toggle={() => setExpandOpen((p) => !p)}
                    size="xl"
                    modalTitle="Power"
                  >
                    <div style={{ height: '60vh', width: '100%' }}>
                      <CustomChartDemo
                        lineChartData={filteredData}
                        unit={"kW"}
                      />
                    </div>
                  </CustomOnlyModal>
                </>
              )
            );
          }
          default:
            return null;
        }
      })()}
    </div>
  );
}

export default PowerConsumptionChartDemo;
